import CategoryIcon from '@/components/cashflow/CategoryIcon';
import MerchantLogo from '@/components/cashflow/MerchantLogo';
import { showRuleModal } from '@/components/cashflow/rules/modal/RuleModal';
import { FlexRow } from '@/components/common/Flex';
import { hoverStyleProps } from '@/components/common/styleHelpers';
import { Circle, FlexProps, Kbd, Text, useToast } from '@chakra-ui/react';
import { getNewTag } from '@common/react/cashflow/getNewTag';
import {
    CreateRuleToastProps,
    getRuleDefaults,
} from '@common/react/cashflow/rules';
import { FC, useCallback, useEffect } from 'react';

export const useCreateRuleToast = () => {
    const toast = useToast();

    return ({ before, after, field }: CreateRuleToastProps) => {
        // Don't show the toast when a tag is removed
        if (field === 'tagNames' && !getNewTag({ before, after })) {
            return;
        }

        toast({
            position: 'top',
            duration: 10_000, // 10 seconds
            containerStyle: {
                mt: 4,
                w: 'calc(100vw - 30%)',
                maxW: 768,
            },
            render: ({ id }) => (
                <CreateRuleToast
                    before={before}
                    after={after}
                    field={field}
                    onClose={() => {
                        if (id) toast.close(id);
                    }}
                />
            ),
        });
    };
};

const toastSectionProps = {
    px: 6,
    py: 4,
    h: 13,
    align: 'center',
};

const CreateRuleToast = ({
    before,
    after,
    field,
    onClose,
}: CreateRuleToastProps & { onClose: () => void }) => {
    const newTag = getNewTag({ before, after });

    const handleCreateRule = useCallback(() => {
        const { defaultConditions, defaultChanges } = getRuleDefaults({
            before,
            after,
            field,
        });

        showRuleModal({
            defaultConditions,
            defaultChanges,
            slideDown: true,
            isGlobalRule: false,
        });

        onClose();
    }, [before, after, field, onClose]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey && e.key.toLowerCase() === 'r') {
                handleCreateRule();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleCreateRule, onClose]);

    return (
        <FlexRow fontSize="sm" color="white" align="center" borderRadius="md">
            <FlexRow
                gap={3}
                bg="#424141"
                borderTopLeftRadius="md"
                borderBottomLeftRadius="md"
                borderRight="1px solid"
                borderRightColor="black"
                fontWeight="medium"
                {...toastSectionProps}
                flexGrow={6}
            >
                {field === 'isHidden' && <Text>Transaction hidden</Text>}
                {field === 'categoryId' && (
                    <>
                        <Text>Updated to</Text>
                        <CategoryIcon category={after.category} />
                        <Text>{after.category.name}</Text>
                    </>
                )}
                {field === 'merchantId' && (
                    <>
                        <Text>Updated to</Text>
                        <MerchantLogo merchant={after.merchant!} />
                        <Text>{after.merchant!.name}</Text>
                    </>
                )}
                {field === 'tagNames' && newTag && (
                    <>
                        <Text>Added tag:</Text>
                        <FlexRow align="center" gap={2}>
                            <Circle bg={newTag.color} size={2} />
                            <Text>{newTag.name}</Text>
                        </FlexRow>
                    </>
                )}
            </FlexRow>
            <Option onClick={handleCreateRule}>
                Create rule
                <Kbd>Ctrl + R</Kbd>
            </Option>
            <Option
                borderTopRightRadius="md"
                borderBottomRightRadius="md"
                onClick={onClose}
            >
                Dismiss
            </Option>
        </FlexRow>
    );
};

const Option: FC<FlexProps> = ({ children, ...props }) => (
    <FlexRow
        fontWeight="semibold"
        bg="gray.700"
        justify="center"
        borderRight="1px solid"
        borderRightColor="black"
        flexGrow={1}
        gap={2}
        {...hoverStyleProps({ bg: 'black' })}
        {...toastSectionProps}
        {...props}
    >
        {children}
    </FlexRow>
);
